import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type { JSX } from 'react'
import type { Context, ReactNode } from 'react'

// ========================================
// Context Types
// ========================================

/**
 * The engagement context value shared by Comments and Mentions: the
 * engagement object, the reaction-error state pair, and an `extra` slot for
 * feature-specific payload (e.g. a mention's `replies`). Comments use an empty
 * `extra`; the per-feature hooks flatten this shape into their historical
 * return shapes.
 */
export type EngagementContextValue<TEngagement, TExtra extends object> = {
  /** The engagement object (a comment or a mention). */
  engagement: TEngagement
  /** Error message from a reaction attempt, if any. */
  reactionError: string | null
  /** Set the reaction error message. */
  setReactionError: (error: string | null) => void
  /** Feature-specific payload merged in by the feature provider. */
  extra: TExtra
}

export type EngagementProviderProps<TEngagement, TExtra extends object> = {
  engagement: TEngagement
  /** Feature-specific payload; `{}` for features without one. */
  extra: TExtra
  children: ReactNode
}

export type EngagementContextApi<TEngagement, TExtra extends object> = {
  EngagementContext: Context<
    EngagementContextValue<TEngagement, TExtra> | undefined
  >
  EngagementContextProvider: (
    props: EngagementProviderProps<TEngagement, TExtra>,
  ) => JSX.Element
  useEngagementContext: () => EngagementContextValue<TEngagement, TExtra>
}

// ========================================
// Factory
// ========================================

/**
 * Builds a typed engagement context (definition + provider + access hook) for
 * a single feature. This is the one shared implementation behind both
 * `CommentContext` and `MentionContext`: it owns the reaction-error state
 * machine and the provider/throw boilerplate, while each feature instantiates
 * it with its own engagement type and optional `extra` payload.
 *
 * @param providerName Display/error name of the feature provider, e.g.
 *   `'CommentContextProvider'`.
 * @param hookName Name of the feature access hook used in the
 *   used-outside-provider error, e.g. `'useCommentContext'`.
 */
export function createEngagementContext<
  TEngagement,
  TExtra extends object = Record<string, never>,
>({
  providerName,
  hookName,
}: {
  providerName: string
  hookName: string
}): EngagementContextApi<TEngagement, TExtra> {
  const EngagementContext = createContext<
    EngagementContextValue<TEngagement, TExtra> | undefined
  >(undefined)

  EngagementContext.displayName = providerName

  const EngagementContextProvider = ({
    engagement,
    extra,
    children,
  }: EngagementProviderProps<TEngagement, TExtra>): JSX.Element => {
    const [reactionError, setReactionErrorState] = useState<string | null>(null)

    const setReactionError = useCallback((error: string | null) => {
      setReactionErrorState(error)
    }, [])

    const contextValue = useMemo<EngagementContextValue<TEngagement, TExtra>>(
      () => ({ engagement, reactionError, setReactionError, extra }),
      [engagement, reactionError, setReactionError, extra],
    )

    return (
      <EngagementContext.Provider value={contextValue}>
        {children}
      </EngagementContext.Provider>
    )
  }

  EngagementContextProvider.displayName = providerName

  const useEngagementContext = (): EngagementContextValue<
    TEngagement,
    TExtra
  > => {
    const context = useContext(EngagementContext)

    if (context === undefined) {
      throw new Error(
        `${hookName} must be used within a ${providerName}. ` +
          `Wrap your component tree with <${providerName}> to use this hook.`,
      )
    }

    return context
  }

  return { EngagementContext, EngagementContextProvider, useEngagementContext }
}
